import { enqueue, listQueue, newClientId, removeFromQueue } from "@/lib/offlineQueue.js";

const TOKEN_KEY = "orignal2026.token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

const FIELD = ["/api/nordquest", "/api/weather", "/api/day-plan", "/api/tasks", "/api/terrain-points", "/api/expenses"];

function inferKind(path) {
  if (path.includes("/observations")) return "observation";
  if (path.includes("terrain-points")) return "terrain_point";
  if (path.includes("/territory/points")) return "point";
  if (path.includes("/tasks")) return "task";
  if (path.includes("/expenses")) return "expense";
  if (path.includes("/food")) return "food";
  if (path.includes("/equipment")) return "equipment";
  if (path.includes("/checklist")) return "checklist";
  if (path.includes("nordquest/group/none")) return "nordquest_none";
  if (path.includes("/return")) return "return";
  return "note";
}

function rememberSync() {
  const at = new Date().toISOString();
  try { localStorage.setItem("orignal2026.lastSync", at); } catch { /* private mode */ }
  window.dispatchEvent(new CustomEvent("orignal-sync-done", { detail: { at } }));
}

function readField(path) {
  try {
    const raw = localStorage.getItem(`orignal2026.cache.${path}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export async function api(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body && !(options.body instanceof FormData) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  const method = (options.method || "GET").toUpperCase();
  let res;
  try {
    res = await fetch(path, { ...options, headers });
  } catch (err) {
    if (method === "GET") {
      const cached = readField(path.split("?")[0]);
      if (cached) return { ...cached, _cached: true, offline: true };
    }
    err.offline = true;
    throw err;
  }
  if (res.status === 401) {
    const error = new Error("auth_expired");
    error.status = 401;
    throw error;
  }
  if (!res.ok) {
    const error = new Error(`api_${res.status}`);
    error.status = res.status;
    throw error;
  }
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    const data = await res.json();
    if (method === "GET" && FIELD.some((item) => path.startsWith(item))) {
      try { localStorage.setItem(`orignal2026.cache.${path.split("?")[0]}`, JSON.stringify(data)); } catch { /* full storage */ }
      rememberSync();
    }
    return data;
  }
  return res;
}

export async function apiJson(path, body, method = "POST") {
  const clientId = body?.client_id || newClientId();
  const payload = { ...(body || {}), client_id: clientId };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    await enqueue({ client_id: clientId, kind: inferKind(path), path, payload });
    window.dispatchEvent(new CustomEvent("orignal-offline-change"));
    return { _offline: true, client_id: clientId, queued: true, ...payload };
  }
  try {
    return await api(path, { method, body: JSON.stringify(payload) });
  } catch (err) {
    if (err.status === 401 || err.status === 400 || err.status === 403 || err.status === 409) throw err;
    await enqueue({ client_id: clientId, kind: inferKind(path), path, payload });
    window.dispatchEvent(new CustomEvent("orignal-offline-change"));
    return { _offline: true, client_id: clientId, queued: true, ...payload };
  }
}

export async function flushOfflineQueue() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return { flushed: 0 };
  window.dispatchEvent(new CustomEvent("orignal-sync-start"));
  const items = await listQueue();
  let flushed = 0;
  let stopped = false;
  for (const item of items) {
    try {
      if (item.kind === "avenza_import" && item.payload?.b64) {
        const bin = atob(item.payload.b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
        const fd = new FormData();
        fd.append("file", new Blob([bytes]), item.payload.filename || "import.gpx");
        await api("/api/territory/import", { method: "POST", body: fd });
      } else {
        await api("/api/sync/push", { method: "POST", body: JSON.stringify({ kind: item.kind, client_id: item.client_id, payload: item.payload }) });
      }
      await removeFromQueue(item.client_id);
      flushed += 1;
    } catch {
      stopped = true;
      break;
    }
  }
  window.dispatchEvent(new CustomEvent("orignal-offline-change"));
  if (!stopped) rememberSync();
  else window.dispatchEvent(new CustomEvent("orignal-sync-done"));
  return { flushed };
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    flushOfflineQueue().catch(() => {});
  });
}
